import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useClub } from '@/contexts/ClubContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Trophy, ListOrdered, Clock, BarChart3, LineChart as LineChartIcon, Calendar, CheckCircle2, TrendingUp, TrendingDown, Timer, Activity, HelpCircle, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PageSpinner from '@/components/PageSpinner';
import { CardGridSkeleton, PageHeaderSkeleton, StatGridSkeleton, StatsSectionSkeleton } from '@/components/PageSkeletons';
import { billingService, clubService, feedbackService, memberService, matchService, queueService } from '@/services/storageService';
import PlayerInsightsPanel from '@/components/dashboard/PlayerInsightsPanel';
import type { ApiClub, ApiClubMember, ApiQueueEntry, ApiQueueHistoryItem, ApiQueueHistoryResponse, QueueHistoryItem } from '@/types';
import { mapEvaluationsFromApi, mapMatchHistoryFromApi, mapMembersFromApi, mapQueueFromApi } from '@/lib/clubApiMappers';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  XAxis,
  YAxis,
} from 'recharts';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useI18n } from '@/contexts/I18nContext';

const mapQueueHistory = (items: ApiQueueHistoryItem[]): QueueHistoryItem[] =>
  (items || []).map((item) => ({
    id: String(item.session_id),
    memberId: String(item.member_id),
    firstName: item.first_name,
    lastName: item.last_name,
    email: item.email,
    startedAt: item.started_at,
    endedAt: item.ended_at || null,
    durationSeconds: typeof item.duration_seconds === 'number' ? item.duration_seconds : 0,
  }));

const normalizeRankLabel = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-').map((v) => Number(v));
  if (!year || !month || !day) return dateStr;
  const dt = new Date(year, month - 1, day);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const DAY_CHART_WINDOW = 30;
const DAY_RANGE_OPTIONS = [14, 30, 90];

const startOfDay = (date: Date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const formatDayLabel = (date: Date) =>
  date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { activeClubId, currentClubRole } = useClub();
  const clubId = activeClubId ? Number(activeClubId) : null;
  const roleRaw = (currentClubRole || user?.role || '').toLowerCase();
  const role = roleRaw === 'player' ? 'member' : roleRaw;
  const isAdmin = role === 'admin';
  const isCoach = role === 'coach';
  const isMember = role === 'member';
  const [evaluationRangeDays, setEvaluationRangeDays] = React.useState(90);
  const [rankRangeDays, setRankRangeDays] = React.useState(30);
  const [winsRangeDays, setWinsRangeDays] = React.useState(30);
  const [pointsRangeDays, setPointsRangeDays] = React.useState(30);
  const [winsChartPage, setWinsChartPage] = React.useState(0);
  const [pointsChartPage, setPointsChartPage] = React.useState(0);
  const [selectionModeRangeDays, setSelectionModeRangeDays] = React.useState(30);
  const [histogramRangeDays, setHistogramRangeDays] = React.useState(30);
  const [showRankAverage, setShowRankAverage] = React.useState(true);
  const billingQuery = useQuery({
    queryKey: ['billing-status', clubId],
    queryFn: () => billingService.getStatus(clubId),
    enabled: Boolean(user && clubId),
  });
  const resolvedPackageCode = String(
    billingQuery.data?.package || billingQuery.data?.recommended_package || user?.packageType || 'package1',
  )
    .trim()
    .toLowerCase();
  const isInsightGraphsAvailable = resolvedPackageCode !== 'package1';

  const clubQuery = useQuery({
    queryKey: ['club', clubId],
    queryFn: () => clubService.get(clubId as number) as Promise<ApiClub & { courts?: { is_active?: boolean }[] }>,
    enabled: Boolean(clubId),
  });

  const membersQuery = useQuery({
    queryKey: ['club-members', clubId],
    queryFn: () => memberService.list(clubId as number) as Promise<ApiClubMember[]>,
    enabled: Boolean(clubId),
  });

  const queueQuery = useQuery({
    queryKey: ['club-queue', clubId],
    queryFn: () => queueService.list(clubId as number) as Promise<ApiQueueEntry[]>,
    enabled: Boolean(clubId),
  });

  const queueHistoryQuery = useQuery({
    queryKey: ['queue-history-all', clubId],
    queryFn: async () => {
      if (!clubId) return [] as QueueHistoryItem[];
      const pageSize = 200;
      let page = 1;
      let all: QueueHistoryItem[] = [];
      let total = 0;

      while (true) {
        const res = (await queueService.history(clubId, page, pageSize)) as ApiQueueHistoryResponse;
        const mapped = mapQueueHistory(res.items || []);
        total = res.total || mapped.length;
        all = [...all, ...mapped];

        if (all.length >= total || mapped.length < pageSize) break;
        page += 1;
      }

      return all;
    },
    enabled: Boolean(clubId),
  });

  const evaluationsQuery = useQuery({
    queryKey: ['player-evaluations', clubId, user?.id],
    queryFn: () => {
      if (!clubId || !user?.id) return [];
      return feedbackService.listForPlayer(user.id, clubId);
    },
    enabled: Boolean(clubId && user?.id && isInsightGraphsAvailable),
  });

  const leaderboardHistoryQuery = useQuery({
    queryKey: ['leaderboard-history', clubId, user?.id, rankRangeDays],
    queryFn: () => {
      if (!clubId || !user?.id) return null;
      return matchService.getLeaderboardHistory(clubId, user.id, rankRangeDays);
    },
    enabled: Boolean(clubId && user?.id && isInsightGraphsAvailable),
  });

  const historyQuery = useQuery({
    queryKey: ['club-history', clubId],
    queryFn: () => matchService.listHistoryByClub(clubId as number) as Promise<any[]>,
    enabled: Boolean(clubId),
  });

  const club = clubQuery.data;
  const members = useMemo(
    () => mapMembersFromApi((membersQuery.data || []) as ApiClubMember[]),
    [membersQuery.data],
  );

  const queue = useMemo(() => mapQueueFromApi((queueQuery.data || []) as ApiQueueEntry[]), [queueQuery.data]);

  const matchHistoryRaw = useMemo(() => (Array.isArray(historyQuery.data) ? historyQuery.data : []), [historyQuery.data]);
  const matchHistory = useMemo(
    () => mapMatchHistoryFromApi(matchHistoryRaw as any[], members),
    [matchHistoryRaw, members],
  );
  const isHeaderLoading = Boolean(clubId) && clubQuery.isLoading;
  const isStatsLoading = Boolean(clubId) && (membersQuery.isLoading || queueQuery.isLoading || historyQuery.isLoading);
  const isActiveMatchesLoading = Boolean(clubId) && (membersQuery.isLoading || historyQuery.isLoading);
  const isClubStatsLoading =
    Boolean(clubId) && (historyQuery.isLoading || queueQuery.isLoading || queueHistoryQuery.isLoading);
  const isPersonalizedStatsLoading =
    Boolean(clubId) && (membersQuery.isLoading || historyQuery.isLoading || queueHistoryQuery.isLoading);

  const today = new Date().toDateString();
  const currentMember = useMemo(() => {
    if (!user) return undefined;
    const byId = members.find((m) => m.userId === String(user.id) || m.id === String(user.id));
    if (byId) return byId;
    return members.find((m) => m.email && user.email && m.email.toLowerCase() === user.email.toLowerCase());
  }, [members, user]);

  const memberQueueEntry = useMemo(
    () => (currentMember ? queue.find((q) => String(q.userId) === currentMember.id) : undefined),
    [queue, currentMember],
  );
  const isQueued = memberQueueEntry?.status === 'QUEUED';
  const isPlaying = memberQueueEntry?.status === 'PLAYING';
  const queueStatusLabel = isQueued ? 'In Queue' : isPlaying ? 'Playing' : 'Not in Queue';
  const queueStatusColor = isQueued ? 'text-success' : isPlaying ? 'text-accent' : 'text-muted-foreground';
  const queueStatusBg = isQueued ? 'bg-success/10' : isPlaying ? 'bg-accent/10' : 'bg-muted/50';

  const isInQueue = isQueued;

  const memberTodayMatches = useMemo(() => {
    if (!currentMember) return 0;
    return matchHistory.filter((m: any) => {
      const ts = m.timestamp || m.finishedAt || m.startedAt;
      if (!ts) return false;
      const players = Array.isArray(m.players) ? m.players.map((p: any) => String(p)) : [];
      return new Date(ts).toDateString() === today && players.includes(currentMember.id);
    }).length;
  }, [matchHistory, today, currentMember]);

  const ongoingMatches = useMemo(
    () => matchHistory.filter((m: any) => m.status === 'ongoing' || m.isActive),
    [matchHistory],
  );

  const completedMatches = useMemo(
    () => matchHistory.filter((m: any) => m.status === 'finished' || Boolean(m.finishedAt)),
    [matchHistory],
  );

  const activeMatchesForUser = useMemo(() => {
    if (isAdmin || isCoach) return ongoingMatches;
    if (!currentMember) return [];
    return ongoingMatches.filter((m: any) =>
      [...(m.team1 || []), ...(m.team2 || []), ...(m.players || [])].some(
        (pid: string) => String(pid) === String(currentMember.id),
      ),
    );
  }, [currentMember, ongoingMatches, isAdmin, isCoach]);

  const myMatchHistory = useMemo(() => {
    if (!currentMember) return [];
    return matchHistory.filter((m: any) =>
      [...(m.team1 || []), ...(m.team2 || []), ...(m.players || [])].some(
        (pid: string) => String(pid) === String(currentMember.id),
      ),
    );
  }, [currentMember, matchHistory]);

  const todayMatches = useMemo(
    () =>
      completedMatches.filter((m: any) => {
        const ts = m.finishedAt || m.timestamp || m.startedAt;
        if (!ts) return false;
        return new Date(ts).toDateString() === today;
      }),
    [completedMatches, today],
  );

  const userCompletedMatches = useMemo(
    () => myMatchHistory.filter((m: any) => m.status === 'finished' || Boolean(m.finishedAt)),
    [myMatchHistory],
  );

  const userMatchesToday = useMemo(
    () =>
      userCompletedMatches.filter((m: any) => {
        const ts = m.finishedAt || m.timestamp || m.startedAt;
        if (!ts) return false;
        return new Date(ts).toDateString() === today;
      }),
    [userCompletedMatches, today],
  );

  const userWinsToday = useMemo(() => {
    if (!currentMember) return 0;
    return userMatchesToday.filter((m: any) => {
      const isTeam1 = (m.team1 || []).some((id: string) => String(id) === String(currentMember.id));
      const isTeam2 = (m.team2 || []).some((id: string) => String(id) === String(currentMember.id));
      if (!isTeam1 && !isTeam2) return false;
      return isTeam1 ? m.score.team1 > m.score.team2 : m.score.team2 > m.score.team1;
    }).length;
  }, [currentMember, userMatchesToday]);

  React.useEffect(() => {
    setWinsChartPage(0);
  }, [winsRangeDays]);

  React.useEffect(() => {
    setPointsChartPage(0);
  }, [pointsRangeDays]);

  const lastResults = useMemo(() => {
    if (!currentMember) return [] as { label: string; won: number; lost: number; ts: number }[];
    const todayDate = startOfDay(new Date());
    const startDate = new Date(todayDate);
    startDate.setDate(startDate.getDate() - (winsRangeDays - 1));
    const dayBuckets = new Map<string, { label: string; won: number; lost: number; ts: number }>();

    userCompletedMatches.forEach((m: any) => {
      const isTeam1 = (m.team1 || []).some((id: string) => String(id) === String(currentMember.id));
      const isTeam2 = (m.team2 || []).some((id: string) => String(id) === String(currentMember.id));
      if (!isTeam1 && !isTeam2) return;
      const won = isTeam1 ? m.score.team1 > m.score.team2 : m.score.team2 > m.score.team1;
      const rawDate = m.finishedAt || m.timestamp || m.startedAt;
      if (!rawDate) return;
      const date = startOfDay(new Date(rawDate));
      const ts = date.getTime();
      if (!Number.isFinite(ts) || ts < startDate.getTime() || ts > todayDate.getTime()) return;
      const key = date.toDateString();
      const entry = dayBuckets.get(key) || { label: formatDayLabel(date), won: 0, lost: 0, ts };
      entry.won += won ? 1 : 0;
      entry.lost += won ? 0 : 1;
      dayBuckets.set(key, entry);
    });

    return Array.from(dayBuckets.values())
      .sort((a, b) => a.ts - b.ts)
      .map(({ label, won, lost, ts }) => ({ label, won, lost, ts }));
  }, [currentMember, userCompletedMatches, winsRangeDays]);

  const lastPoints = useMemo(() => {
    if (!currentMember) return [] as { label: string; scored: number; conceded: number; ts: number }[];
    const todayDate = startOfDay(new Date());
    const startDate = new Date(todayDate);
    startDate.setDate(startDate.getDate() - (pointsRangeDays - 1));
    const dayBuckets = new Map<string, { label: string; scored: number; conceded: number; ts: number }>();

    userCompletedMatches.forEach((m: any) => {
      const isTeam1 = (m.team1 || []).some((id: string) => String(id) === String(currentMember.id));
      const isTeam2 = (m.team2 || []).some((id: string) => String(id) === String(currentMember.id));
      if (!isTeam1 && !isTeam2) return;
      const rawDate = m.finishedAt || m.timestamp || m.startedAt;
      if (!rawDate) return;
      const date = startOfDay(new Date(rawDate));
      const ts = date.getTime();
      if (!Number.isFinite(ts) || ts < startDate.getTime() || ts > todayDate.getTime()) return;
      const key = date.toDateString();
      const entry = dayBuckets.get(key) || { label: formatDayLabel(date), scored: 0, conceded: 0, ts };
      const team1Score = m.score?.team1 ?? 0;
      const team2Score = m.score?.team2 ?? 0;
      if (isTeam1) {
        entry.scored += team1Score;
        entry.conceded += team2Score;
      } else if (isTeam2) {
        entry.scored += team2Score;
        entry.conceded += team1Score;
      }
      dayBuckets.set(key, entry);
    });

    return Array.from(dayBuckets.values())
      .sort((a, b) => a.ts - b.ts)
      .map(({ label, scored, conceded, ts }) => ({ label, scored, conceded, ts }));
  }, [currentMember, userCompletedMatches, pointsRangeDays]);

  const winsVisibleDays = winsRangeDays > DAY_CHART_WINDOW ? DAY_CHART_WINDOW : winsRangeDays;
  const pointsVisibleDays = pointsRangeDays > DAY_CHART_WINDOW ? DAY_CHART_WINDOW : pointsRangeDays;
  const winsCanPage = winsRangeDays > DAY_CHART_WINDOW && lastResults.length > DAY_CHART_WINDOW;
  const pointsCanPage = pointsRangeDays > DAY_CHART_WINDOW && lastPoints.length > DAY_CHART_WINDOW;
  const winsMaxPage = Math.max(0, Math.ceil(lastResults.length / DAY_CHART_WINDOW) - 1);
  const pointsMaxPage = Math.max(0, Math.ceil(lastPoints.length / DAY_CHART_WINDOW) - 1);

  React.useEffect(() => {
    setWinsChartPage((current) => Math.min(current, winsMaxPage));
  }, [winsMaxPage]);

  React.useEffect(() => {
    setPointsChartPage((current) => Math.min(current, pointsMaxPage));
  }, [pointsMaxPage]);

  const visibleWinsResults = useMemo(() => {
    if (!lastResults.length) return [];
    const end = lastResults.length - (winsChartPage * DAY_CHART_WINDOW);
    const start = Math.max(0, end - winsVisibleDays);
    return lastResults.slice(start, end).map(({ label, won, lost }) => ({ label, won, lost }));
  }, [lastResults, winsChartPage, winsVisibleDays]);

  const visiblePointsResults = useMemo(() => {
    if (!lastPoints.length) return [];
    const end = lastPoints.length - (pointsChartPage * DAY_CHART_WINDOW);
    const start = Math.max(0, end - pointsVisibleDays);
    return lastPoints.slice(start, end).map(({ label, scored, conceded }) => ({ label, scored, conceded }));
  }, [lastPoints, pointsChartPage, pointsVisibleDays]);

  const evaluationHistory = useMemo(
    () => (isInsightGraphsAvailable ? mapEvaluationsFromApi((evaluationsQuery.data || []) as any[]) : []),
    [evaluationsQuery.data, isInsightGraphsAvailable],
  );
  const evaluationChartData = useMemo(() => {
    if (!evaluationHistory.length) return [];
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - (evaluationRangeDays - 1));
    return [...evaluationHistory]
      .sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime())
      .filter((ev) => {
        const ts = new Date(ev.date || 0).getTime();
        return Number.isFinite(ts) && ts >= cutoff.getTime();
      })
      .map((ev) => {
        const date = new Date(ev.date || Date.now());
        const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
        return {
          label,
          technical: Number(ev.categories.technical ?? 0),
          movement: Number(ev.categories.movement ?? 0),
          tactical: Number(ev.categories.tactical ?? 0),
          physical: Number(ev.categories.physical ?? 0),
          psychological: Number(ev.categories.psychological ?? 0),
          average: Number(ev.rating ?? 0),
        };
      });
  }, [evaluationHistory, evaluationRangeDays]);

  const categoryAverages = useMemo(() => {
    const sums: Record<string, { total: number; count: number }> = {
      technical: { total: 0, count: 0 },
      movement: { total: 0, count: 0 },
      tactical: { total: 0, count: 0 },
      physical: { total: 0, count: 0 },
      psychological: { total: 0, count: 0 },
    };
    evaluationHistory.forEach((ev) => {
      const categories = ev.categories || {};
      Object.entries({
        technical: categories.technical,
        movement: categories.movement,
        tactical: categories.tactical,
        physical: categories.physical,
        psychological: categories.psychological,
      }).forEach(([key, value]) => {
        if (typeof value === 'number' && !Number.isNaN(value)) {
          sums[key].total += value;
          sums[key].count += 1;
        }
      });
    });
    return Object.entries(sums)
      .map(([key, { total, count }]) => ({
        key,
        avg: count ? total / count : 0,
      }))
      .sort((a, b) => a.avg - b.avg);
  }, [evaluationHistory]);

  const defaultVisibleCategories = useMemo(() => {
    const weakest = categoryAverages.slice(0, 2).map((entry) => entry.key);
    return new Set(['average', ...weakest]);
  }, [categoryAverages]);

  const [visibleEvalKeys, setVisibleEvalKeys] = React.useState<Set<string>>(new Set());
  React.useEffect(() => {
    setVisibleEvalKeys(defaultVisibleCategories);
  }, [defaultVisibleCategories]);

  const leaderboardHistoryData = useMemo(() => {
    if (!isInsightGraphsAvailable) return [];
    const points = (leaderboardHistoryQuery.data as any)?.points || [];
    const mapped = points.map((point: any) => ({
      label: normalizeRankLabel(point.date),
      rank: point.rank ?? null,
      rating: point.rating ?? null,
      played: point.played ?? 0,
      won: point.won ?? 0,
      lost: point.lost ?? 0,
    }));
    return mapped.map((row: any, index: number) => {
      const windowStart = Math.max(0, index - 6);
      const slice = mapped.slice(windowStart, index + 1).filter((p: any) => p.rank !== null);
      const avg = slice.length ? slice.reduce((sum: number, p: any) => sum + p.rank, 0) / slice.length : null;
      return { ...row, rank_avg: avg !== null ? Math.round(avg * 10) / 10 : null };
    });
  }, [leaderboardHistoryQuery.data, isInsightGraphsAvailable]);

  const currentPlanLabel = resolvedPackageCode === 'package3'
    ? t('package_label_elite', 'Elite (Package 3)')
    : resolvedPackageCode === 'package2'
      ? t('package_label_pro', 'Pro (Package 2)')
      : t('package_label_starter', 'Starter (Package 1)');
  const currentPlanPrice = resolvedPackageCode === 'package3'
    ? `$299/${t('pkg_month', 'month')}`
    : resolvedPackageCode === 'package2'
      ? `$159/${t('pkg_month', 'month')}`
      : `$69/${t('pkg_month', 'month')}`;

  const currentPlanHighlights = resolvedPackageCode === 'package3'
    ? [
      `${t('dashboard_price_label', 'Price')}: $299/${t('pkg_month', 'month')}`,
      'Everything in Pro',
      'Up to 250 active members',
      'Advanced roles and permissions (multiple club admins)',
      'Advanced UI controls (colors/fonts for club branding)',
      'Club logo upload/management',
      'Member profile avatar selection',
      'AI chat for match-history insights',
      'Actionable performance insights on dashboard',
    ]
    : resolvedPackageCode === 'package2'
      ? [
        `${t('dashboard_price_label', 'Price')}: $159/${t('pkg_month', 'month')}`,
        'Everything in Starter',
        'Up to 150 active members',
        'Coach evaluations',
        'Equipment tracking',
        'Limited ML-assisted analytics for key performance trends',
        'Actionable performance insights on dashboard',
        'Daily match reports and advanced leaderboard trends',
      ]
      : [
        `${t('dashboard_price_label', 'Price')}: $69/${t('pkg_month', 'month')}`,
        'Up to 50 active members',
        'Member management',
        'Queue and court assignment',
        'Match history and basic leaderboard',
        'Basic admin dashboard',
      ];

  const renderLockedInsightPlaceholder = (
    title: string,
    hint: string,
    kind: 'evaluation' | 'rank' | 'profile',
  ) => {
    const insightMeta = {
      evaluation: { label: t('dashboard_eval_analytics', 'Evaluation Analytics'), icon: LineChartIcon },
      rank: { label: t('dashboard_rank_analytics', 'Ranking Analytics'), icon: Trophy },
      profile: { label: t('dashboard_profile_analytics', 'Profile Analytics'), icon: BarChart3 },
    }[kind];
    const InsightIcon = insightMeta.icon;

    return (
      <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-4 sm:p-5">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10" />
        <div className="relative">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1.5">
              <h4 className="text-[18px] font-bold text-red-500 text-foreground">
                {t('dashboard_visual_unavailable', 'Visual unavailable')}
              </h4>
              <p className="text-sm text-muted-foreground">{hint}</p>
            </div>
            <span className="inline-flex items-center border-red-500 text-red-500 gap-1 rounded-full border border-border bg-background/80 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
              <Lock size={12} />
              {t('dashboard_locked_in_starter', 'Locked in Starter')}
            </span>
          </div>


          <div className="mt-4 border border-border rounded-xl bg-muted/35 p-3">
            <p className="text-[11px] text-bold uppercase tracking-wide text-muted-foreground">
              {t('dashboard_current_plan', 'Current Plan')}
            </p>
            <p className="mt-1 text-sm font-semibold text-foreground">{currentPlanLabel}</p>
            <p className="mt-1 text-sm font-medium text-foreground">{currentPlanPrice}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {currentPlanHighlights.map((item) => (
                <span key={item} className="rounded-full border border-border bg-background px-2 py-1 text-[12px] text-muted-foreground">
                  {item}
                </span>
              ))}
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Available on Pro{' '}
              <Link to="/dashboard/packages#package2" className="font-medium underline underline-offset-2">
                (Package 2)
              </Link>{' '}
              and Elite{' '}
              <Link to="/dashboard/packages#package3" className="font-medium underline underline-offset-2">
                (Package 3)
              </Link>
              .
            </p>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button asChild variant="outline" size="sm" className="h-9">
              <Link to="/dashboard/packages">View Package Details</Link>
            </Button>
            {isAdmin ? (
              <Button asChild size="sm" className="h-9">
                <Link to="/dashboard/club">Upgrade Package</Link>
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">Please contact your club admin to unlock this insight.</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const modeLabel = (mode?: string) => {
    const normalized = String(mode || 'UNKNOWN').toUpperCase();
    if (normalized === 'ROUND_ROBIN') return 'Round Robin';
    if (normalized === 'SKILL_BASED') return 'Skill Based';
    if (normalized === 'BALANCED_TEAM') return 'Balanced Team';
    if (normalized === 'PLAYERS_PICK') return 'Players Pick';
    return 'Unknown';
  };

  const stackedModeData = useMemo(() => {
    const source = isMember ? userCompletedMatches : completedMatches;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 13);

    const buckets = new Map<
      string,
      {
        label: string;
        ts: number;
        round_robin: number;
        skill_based: number;
        balanced_team: number;
        players_pick: number;
        unknown: number;
      }
    >();

    source.forEach((m: any) => {
      const tsRaw = m.finishedAt || m.timestamp || m.startedAt;
      if (!tsRaw) return;
      const ts = new Date(tsRaw).getTime();
      if (!Number.isFinite(ts) || ts < cutoff.getTime()) return;
      const date = new Date(ts);
      const key = date.toDateString();
      const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const current =
        buckets.get(key) || {
          label,
          ts,
          round_robin: 0,
          skill_based: 0,
          balanced_team: 0,
          players_pick: 0,
          unknown: 0,
        };

      const mode = String(m.selectionMechanism || 'UNKNOWN').toUpperCase();
      if (mode === 'ROUND_ROBIN') current.round_robin += 1;
      else if (mode === 'SKILL_BASED') current.skill_based += 1;
      else if (mode === 'BALANCED_TEAM') current.balanced_team += 1;
      else if (mode === 'PLAYERS_PICK') current.players_pick += 1;
      else current.unknown += 1;
      current.ts = Math.min(current.ts, ts);
      buckets.set(key, current);
    });

    return Array.from(buckets.values()).sort((a, b) => a.ts - b.ts);
  }, [isMember, userCompletedMatches, completedMatches]);

  const selectionModeShareData = useMemo(() => {
    const source = isMember ? userCompletedMatches : completedMatches;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - (selectionModeRangeDays - 1));

    const filtered = source.filter((m: any) => {
      const tsRaw = m.finishedAt || m.timestamp || m.startedAt;
      if (!tsRaw) return false;
      const ts = new Date(tsRaw).getTime();
      return Number.isFinite(ts) && ts >= cutoff.getTime();
    });

    const totals = filtered.reduce(
      (acc, row) => {
        const mode = String(row.selectionMechanism || 'UNKNOWN').toUpperCase();
        if (mode === 'ROUND_ROBIN') acc.round_robin += 1;
        else if (mode === 'SKILL_BASED') acc.skill_based += 1;
        else if (mode === 'BALANCED_TEAM') acc.balanced_team += 1;
        else if (mode === 'PLAYERS_PICK') acc.players_pick += 1;
        else acc.unknown += 1;
        return acc;
      },
      { round_robin: 0, skill_based: 0, balanced_team: 0, players_pick: 0, unknown: 0 },
    );

    return [
      { key: 'round_robin', name: modeLabel('ROUND_ROBIN'), value: totals.round_robin, fill: 'hsl(var(--sidebar-primary))' },
      { key: 'skill_based', name: modeLabel('SKILL_BASED'), value: totals.skill_based, fill: 'hsl(var(--success))' },
      { key: 'balanced_team', name: modeLabel('BALANCED_TEAM'), value: totals.balanced_team, fill: 'hsl(var(--warning))' },
      { key: 'players_pick', name: modeLabel('PLAYERS_PICK'), value: totals.players_pick, fill: 'hsl(var(--accent))' },
      { key: 'unknown', name: modeLabel('UNKNOWN'), value: totals.unknown, fill: 'hsl(var(--muted-foreground))' },
    ].filter((row) => row.value > 0);
  }, [isMember, userCompletedMatches, completedMatches, selectionModeRangeDays]);

  const queueWaitHistogramData = useMemo(() => {
    if (!isAdmin) return [];
    const history = (queueHistoryQuery.data || []) as QueueHistoryItem[];
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - (histogramRangeDays - 1));
    const durations = history
      .filter((h) => {
        const ts = new Date(h.startedAt || 0).getTime();
        return Number.isFinite(ts) && ts >= cutoff.getTime();
      })
      .map((h) => (typeof h.durationSeconds === 'number' ? h.durationSeconds : 0))
      .filter((v) => v > 0);

    if (!durations.length) return [];

    const bins = [0, 300, 600, 900, 1200, 1800, 2400, 3600];
    const labels = ['0-5m', '5-10m', '10-15m', '15-20m', '20-30m', '30-40m', '40-60m', '60m+'];
    const counts = Array.from({ length: labels.length }, () => 0);

    durations.forEach((seconds) => {
      let placed = false;
      for (let idx = 0; idx < bins.length - 1; idx += 1) {
        if (seconds >= bins[idx] && seconds < bins[idx + 1]) {
          counts[idx] += 1;
          placed = true;
          break;
        }
      }
      if (!placed) counts[counts.length - 1] += 1;
    });

    return labels.map((label, idx) => ({ label, sessions: counts[idx] }));
  }, [isAdmin, queueHistoryQuery.data, histogramRangeDays]);

  const courtHeatmap = useMemo(() => {
    if (!isAdmin) return { days: [] as string[], rows: [] as { court: number; values: number[] }[], max: 0 };

    const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const courtCount = typeof club?.num_courts === 'number' ? Math.max(1, club.num_courts) : 1;
    const matrix = Array.from({ length: courtCount }, () => Array.from({ length: 7 }, () => 0));

    completedMatches.forEach((m: any) => {
      const tsRaw = m.finishedAt || m.timestamp || m.startedAt;
      if (!tsRaw) return;
      const date = new Date(tsRaw);
      if (!Number.isFinite(date.getTime())) return;
      const day = date.getDay();
      const fallbackCourt = Number.isFinite(Number(m.courtNumber)) ? Number(m.courtNumber) : 1;
      const courtIndex = Math.max(0, Math.min(courtCount - 1, fallbackCourt - 1));
      matrix[courtIndex][day] += 1;
    });

    const max = matrix.flat().reduce((acc, val) => Math.max(acc, val), 0);
    const rows = matrix.map((values, idx) => ({ court: idx + 1, values }));
    return { days: dayLabels, rows, max };
  }, [isAdmin, club?.num_courts, completedMatches]);

  const radarData = useMemo(() => {
    const source = evaluationHistory;
    if (!source.length) return [];

    const categories = [
      { key: 'technical', label: 'Technical' },
      { key: 'movement', label: 'Movement' },
      { key: 'tactical', label: 'Tactical' },
      { key: 'physical', label: 'Physical' },
      { key: 'psychological', label: 'Psychological' },
    ];

    return categories.map((category) => {
      const values = source
        .map((ev: any) => Number(ev.categories?.[category.key] ?? 0))
        .filter((v: number) => Number.isFinite(v) && v > 0);
      const avg = values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
      return { category: category.label, value: Math.round(avg * 100) / 100 };
    });
  }, [evaluationHistory]);

  const kpiSparklineCards = useMemo(() => {
    const dayWindow = 14;
    const days = Array.from({ length: dayWindow }, (_, idx) => {
      const date = new Date();
      date.setDate(date.getDate() - (dayWindow - idx - 1));
      return {
        key: date.toDateString(),
        label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      };
    });
    const sourceMatches = isMember ? userCompletedMatches : completedMatches;
    const matchesByDay = new Map<string, number>();
    const clubMatchesByDay = new Map<string, number>();
    sourceMatches.forEach((m: any) => {
      const tsRaw = m.finishedAt || m.timestamp || m.startedAt;
      if (!tsRaw) return;
      const date = new Date(tsRaw);
      if (!Number.isFinite(date.getTime())) return;
      const key = date.toDateString();
      matchesByDay.set(key, (matchesByDay.get(key) || 0) + 1);
    });
    completedMatches.forEach((m: any) => {
      const tsRaw = m.finishedAt || m.timestamp || m.startedAt;
      if (!tsRaw) return;
      const date = new Date(tsRaw);
      if (!Number.isFinite(date.getTime())) return;
      const key = date.toDateString();
      clubMatchesByDay.set(key, (clubMatchesByDay.get(key) || 0) + 1);
    });

    const matchTrend = days.map((d) => ({ label: d.label, value: matchesByDay.get(d.key) || 0 }));
    const totalMatchesAllTime = sourceMatches.length;
    const courtCount = typeof club?.num_courts === 'number' && club.num_courts > 0 ? club.num_courts : 0;
    const clubMatchDaysAllTime = clubMatchesByDay.size;
    const avgMatchesPerCourtPerDay =
      clubMatchDaysAllTime && courtCount ? completedMatches.length / (clubMatchDaysAllTime * courtCount) : 0;
    const avgMatchesPerCourtTrend = days.map((d) => ({
      label: d.label,
      value: courtCount ? Math.round(((clubMatchesByDay.get(d.key) || 0) / courtCount) * 100) / 100 : 0,
    }));

    const queueHistory = ((queueHistoryQuery.data || []) as QueueHistoryItem[]).filter((h) => {
      const key = h.startedAt ? new Date(h.startedAt).toDateString() : '';
      return days.some((d) => d.key === key);
    });
    const scopedQueueHistory = isMember && currentMember
      ? queueHistory.filter((h) => String(h.memberId) === String(currentMember.id))
      : queueHistory;

    const queueByDay = new Map<string, number[]>();
    scopedQueueHistory.forEach((item) => {
      const date = item.startedAt ? new Date(item.startedAt) : null;
      if (!date || !Number.isFinite(date.getTime())) return;
      const key = date.toDateString();
      if (!key) return;
      if (!queueByDay.has(key)) queueByDay.set(key, []);
      queueByDay.get(key)?.push(Math.max(0, Number(item.durationSeconds || 0)));
    });
    const queueTrend = days.map((d) => {
      const samples = queueByDay.get(d.key) || [];
      const avgMinutes = samples.length
        ? samples.reduce((sum, seconds) => sum + seconds, 0) / samples.length / 60
        : 0;
      return { label: d.label, value: Math.round(avgMinutes * 10) / 10 };
    });
    const queueAvg =
      queueTrend.reduce((sum, row) => sum + row.value, 0) / Math.max(1, queueTrend.filter((r) => r.value > 0).length || 1);

    const durationByDay = new Map<string, number[]>();
    completedMatches.forEach((m: any) => {
      const tsRaw = m.finishedAt || m.timestamp || m.startedAt;
      if (!tsRaw) return;
      const date = new Date(tsRaw);
      if (!Number.isFinite(date.getTime())) return;
      const key = date.toDateString();
      if (!days.some((d) => d.key === key)) return;

      let durationSeconds = Number(m.durationSeconds || 0);
      if (!durationSeconds && m.startedAt && m.finishedAt) {
        const diff = (new Date(m.finishedAt).getTime() - new Date(m.startedAt).getTime()) / 1000;
        durationSeconds = diff > 0 ? diff : 0;
      }
      if (durationSeconds <= 0) return;

      if (!durationByDay.has(key)) durationByDay.set(key, []);
      durationByDay.get(key)?.push(durationSeconds / 60);
    });
    const durationTrend = days.map((d) => {
      const values = durationByDay.get(d.key) || [];
      const avg = values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
      return { label: d.label, value: Math.round(avg * 10) / 10 };
    });

    const allDurationMinutes = completedMatches
      .map((m: any) => {
        let durationSeconds = Number(m.durationSeconds || 0);
        if (!durationSeconds && m.startedAt && m.finishedAt) {
          const diff = (new Date(m.finishedAt).getTime() - new Date(m.startedAt).getTime()) / 1000;
          durationSeconds = diff > 0 ? diff : 0;
        }
        return durationSeconds > 0 ? durationSeconds / 60 : 0;
      })
      .filter((value: number) => value > 0);
    const avgMatchDuration = allDurationMinutes.length
      ? allDurationMinutes.reduce((sum: number, value: number) => sum + value, 0) / allDurationMinutes.length
      : 0;

    return [
      {
        key: 'matches',
        label: t('dashboard_club_matches', 'Club Matches'),
        value: totalMatchesAllTime,
        helper: t('dashboard_completed_matches_selected_club', 'Completed matches in selected club'),
        trend: matchTrend,
      },
      {
        key: 'avg-matches-per-court',
        label: t('dashboard_avg_matches_per_court_day', 'Avg Matches per Court / Day'),
        value: avgMatchesPerCourtPerDay.toFixed(2),
        helper: t('dashboard_completed_matches_divided_by_courts', 'Completed matches divided by courts across active match days'),
        trend: avgMatchesPerCourtTrend,
      },
      {
        key: 'queue',
        label: t('dashboard_avg_queue_wait_min', 'Avg Queue Wait (min)'),
        value: Math.round(queueAvg * 10) / 10,
        helper: t('dashboard_from_club_queue_sessions', 'From club queue sessions'),
        trend: queueTrend,
      },
      {
        key: 'duration',
        label: t('dashboard_avg_match_duration', 'Avg Match Duration'),
        value: `${Math.round(avgMatchDuration * 10) / 10} min`,
        helper: t('dashboard_average_duration_completed_club_matches', 'Average duration of completed club matches'),
        trend: durationTrend,
      },
    ];
  }, [isMember, userCompletedMatches, completedMatches, currentMember, queueHistoryQuery.data, club?.num_courts]);

  const maxWinsInADay = useMemo(() => {
    if (!currentMember) return 0;
    const winsByDay = new Map<string, number>();
    userCompletedMatches.forEach((m: any) => {
      const isTeam1 = (m.team1 || []).some((id: string) => String(id) === String(currentMember.id));
      const isTeam2 = (m.team2 || []).some((id: string) => String(id) === String(currentMember.id));
      if (!isTeam1 && !isTeam2) return;
      const won = isTeam1 ? m.score.team1 > m.score.team2 : m.score.team2 > m.score.team1;
      if (!won) return;
      const ts = m.finishedAt || m.timestamp || m.startedAt;
      if (!ts) return;
      const key = new Date(ts).toDateString();
      winsByDay.set(key, (winsByDay.get(key) || 0) + 1);
    });
    return winsByDay.size ? Math.max(...winsByDay.values()) : 0;
  }, [currentMember, userCompletedMatches]);

  const maxLossesInADay = useMemo(() => {
    if (!currentMember) return 0;
    const lossesByDay = new Map<string, number>();
    userCompletedMatches.forEach((m: any) => {
      const isTeam1 = (m.team1 || []).some((id: string) => String(id) === String(currentMember.id));
      const isTeam2 = (m.team2 || []).some((id: string) => String(id) === String(currentMember.id));
      if (!isTeam1 && !isTeam2) return;
      const won = isTeam1 ? m.score.team1 > m.score.team2 : m.score.team2 > m.score.team1;
      if (won) return;
      const ts = m.finishedAt || m.timestamp || m.startedAt;
      if (!ts) return;
      const key = new Date(ts).toDateString();
      lossesByDay.set(key, (lossesByDay.get(key) || 0) + 1);
    });
    return lossesByDay.size ? Math.max(...lossesByDay.values()) : 0;
  }, [currentMember, userCompletedMatches]);

  const pointsPerMatch = useMemo(() => {
    if (!currentMember) return { scored: 0, net: 0 };
    const matches = userCompletedMatches.filter((m: any) => m.status === 'finished' || Boolean(m.finishedAt));
    if (!matches.length) return { scored: 0, net: 0 };
    let scored = 0;
    let against = 0;
    matches.forEach((m: any) => {
      const isTeam1 = (m.team1 || []).some((id: string) => String(id) === String(currentMember.id));
      const isTeam2 = (m.team2 || []).some((id: string) => String(id) === String(currentMember.id));
      if (!isTeam1 && !isTeam2) return;
      const team1Score = m.score?.team1 ?? 0;
      const team2Score = m.score?.team2 ?? 0;
      if (isTeam1) {
        scored += team1Score;
        against += team2Score;
      } else if (isTeam2) {
        scored += team2Score;
        against += team1Score;
      }
    });
    const perMatch = matches.length ? scored / matches.length : 0;
    const netPerMatch = matches.length ? (scored - against) / matches.length : 0;
    return { scored: Math.round(perMatch * 100) / 100, net: Math.round(netPerMatch * 100) / 100 };
  }, [currentMember, userCompletedMatches]);

  const formatDuration = (seconds?: number | null) => {
    const totalSeconds = typeof seconds === 'number' && seconds > 0 ? Math.round(seconds) : 0;
    if (!totalSeconds) return '0s';
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return [
      hrs ? `${hrs}h` : null,
      mins ? `${mins}m` : null,
      secs ? `${secs}s` : null,
    ]
      .filter(Boolean)
      .join(' ');
  };

  const average = (values: number[]) => {
    const valid = values.filter((v) => typeof v === 'number' && !Number.isNaN(v));
    if (!valid.length) return 0;
    return valid.reduce((sum, val) => sum + val, 0) / valid.length;
  };

  const dayKey = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString().slice(0, 10);
  };

  const parseStart = (value?: string | null) => {
    if (!value) return null;
    const ts = Date.parse(value);
    return Number.isNaN(ts) ? null : ts;
  };

  const courtStats = useMemo(() => {
    const finishedMatches = (matchHistory || []).filter(
      (m: any) => m.status === 'finished' || m.finishedAt || m.durationSeconds,
    );

    const durations = finishedMatches
      .map((m: any) => {
        if (typeof m.durationSeconds === 'number') return m.durationSeconds;
        if (m.startedAt && m.finishedAt) {
          const diff = (new Date(m.finishedAt).getTime() - new Date(m.startedAt).getTime()) / 1000;
          return diff > 0 ? diff : 0;
        }
        return 0;
      })
      .filter((v: number) => v > 0);

    const perDay: Record<string, { courts: Set<string>; matches: number }> = {};
    finishedMatches.forEach((m: any) => {
      const key = dayKey(m.finishedAt || m.startedAt || m.timestamp);
      if (!key) return;
      if (!perDay[key]) perDay[key] = { courts: new Set<string>(), matches: 0 };

      const courtKey = m.courtId || (m.courtNumber ? String(m.courtNumber) : undefined);
      if (courtKey) perDay[key].courts.add(String(courtKey));

      perDay[key].matches += 1;
    });


    const perDayEntries = Object.values(perDay);
    const daysWithMatches = perDayEntries.length;
    const avgActiveCourtsPerDay = daysWithMatches
      ? perDayEntries.reduce((sum, entry) => sum + entry.courts.size, 0) / daysWithMatches
      : 0;
    const totalMatches = finishedMatches.length;
    const courtCount = typeof club?.num_courts === 'number' ? club.num_courts : 0;
    const avgMatchesPerCourtPerDay =
      daysWithMatches && courtCount ? totalMatches / (daysWithMatches * courtCount) : 0;

    return {
      avgMatchDuration: durations.length
        ? durations.reduce((sum: number, val: number) => sum + val, 0) / durations.length
        : 0,
      avgActiveCourtsPerDay,
      avgMatchesPerCourtPerDay,
    };
  }, [matchHistory, club?.num_courts]);

  const queueStats = useMemo(() => {
    const history = (queueHistoryQuery.data || []) as QueueHistoryItem[];
    const now = Date.now();

    const sessionDurations = history
      .map((h) => (typeof h.durationSeconds === 'number' ? h.durationSeconds : 0))
      .filter((d) => d > 0);

    const durationsByDay: Record<string, number[]> = {};
    history.forEach((h) => {
      const key = dayKey(h.startedAt);
      if (!key) return;
      const duration = typeof h.durationSeconds === 'number' ? h.durationSeconds : 0;
      if (duration <= 0) return;
      if (!durationsByDay[key]) durationsByDay[key] = [];
      durationsByDay[key].push(duration);
    });

    const dailyAverages = Object.values(durationsByDay).map((values) => average(values));
    const avgQueueDuration = dailyAverages.length ? average(dailyAverages) : 0;

    const liveWaits = (queue || [])
      .filter((q) => q.status === 'QUEUED')
      .map((q) => {
        if (typeof q.elapsedSeconds === 'number') return q.elapsedSeconds;
        if (typeof q.lastQueueSeconds === 'number') return q.lastQueueSeconds;
        const started = parseStart(q.waitStartedAt);
        if (started) return Math.max(0, Math.floor((now - started) / 1000));
        return 0;
      })
      .filter((v) => v > 0);

    const waitSamples = [...liveWaits, ...sessionDurations];
    const avgToCourtStart = waitSamples.length ? average(waitSamples) : 0;

    const membersPerDay: Record<string, Set<string>> = {};
    for (const h of history) {
      const key = dayKey(h.startedAt);
      if (!key) continue;
      const memberKey = String(h.memberId ?? '');
      if (!memberKey) continue;
      if (!membersPerDay[key]) membersPerDay[key] = new Set<string>();
      membersPerDay[key].add(memberKey);
    }

    const dayKeys = Object.keys(membersPerDay);
    const daysWithQueue = dayKeys.length;
    const avgDailyQueueCount = daysWithQueue
      ? dayKeys.reduce((sum, k) => sum + membersPerDay[k].size, 0) / daysWithQueue
      : 0;

    return {
      avgQueueDuration,
      avgToCourtStart,
      avgDailyQueueCount,
    };
  }, [queueHistoryQuery.data, queue, average, dayKey, parseStart]);

  const userQueueStatsToday = useMemo(() => {
    if (!currentMember) {
      return { avgTimeInQueue: 0, avgWaitForMatch: 0 };
    }

    const history = (queueHistoryQuery.data || []) as QueueHistoryItem[];
    const todayKey = new Date().toDateString();
    const todaysSessions = history.filter((h) => {
      if (String(h.memberId) !== String(currentMember.id)) return false;
      const started = h.startedAt ? new Date(h.startedAt).toDateString() : '';
      return started === todayKey;
    });

    const sessionDurations = todaysSessions
      .map((h) => (typeof h.durationSeconds === 'number' ? h.durationSeconds : 0))
      .filter((d) => d > 0);

    const avgTimeInQueue = sessionDurations.length ? average(sessionDurations) : 0;

    const liveWaits: number[] = [];
    if (memberQueueEntry?.status === 'QUEUED') {
      const started = parseStart(memberQueueEntry.waitStartedAt);
      if (started) {
        const startedDay = new Date(started).toDateString();
        if (startedDay === todayKey) {
          const liveSeconds =
            typeof memberQueueEntry.elapsedSeconds === 'number'
              ? memberQueueEntry.elapsedSeconds
              : Math.max(0, Math.floor((Date.now() - started) / 1000));
          if (liveSeconds > 0) liveWaits.push(liveSeconds);
        }
      }
    }

    const waitSamples = [...sessionDurations, ...liveWaits];
    const avgWaitForMatch = waitSamples.length ? average(waitSamples) : 0;

    return { avgTimeInQueue, avgWaitForMatch };
  }, [currentMember, queueHistoryQuery.data, memberQueueEntry, average, parseStart]);

  const welcomeName = useMemo(() => {
    const memberName = currentMember
      ? currentMember.nickName?.trim() || `${currentMember.firstName} ${currentMember.lastName}`.trim()
      : '';
    if (memberName) return memberName;
    if (user) {
      const fallback = user.nickname?.trim() || `${user.firstName} ${user.lastName}`.trim();
      if (fallback) return fallback;
    }
    return '';
  }, [currentMember, user]);

  const stats = useMemo(() => {
    if (role === 'member') {
      return [
        {
          label: t('dashboard_my_matches_today', 'My Matches Today'),
          value: memberTodayMatches,
          icon: Trophy,
          color: 'text-primary',
          bgColor: 'bg-primary/10',
        },
        {
          label: t('dashboard_queue_status', 'Queue Status'),
          value: queueStatusLabel,
          icon: ListOrdered,
          color: queueStatusColor,
          bgColor: queueStatusBg,
        },
      ];
    }

    if (role === 'coach') {
      return [];
    }

    // admin default
    return [
      {
        label: t('dashboard_total_members', 'Total Members'),
        value: members.length,
        icon: Users,
        color: 'text-primary',
        bgColor: 'bg-primary/10',
      },
    ];
  }, [
    role,
    members.length,
    memberTodayMatches,
    queueStatusLabel,
    queueStatusColor,
    queueStatusBg,
  ]);

  const formatPlayerInline = (match: any, playerId: string) => {
    const id = String(playerId);
    const fallback = match.playerNames?.[id];
    const member = members.find((m) => m.id === id || m.userId === id) || fallback;
    if (!member) return `Player ${id}`;
    const first = member.firstName || '';
    const last = member.lastName || '';
    return `${first} ${last}`.trim();
  };

  if (!clubId && user?.role === 'admin') {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <Card className="text-center">
          <CardContent className="pt-12 pb-8">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">🏸</span>
            </div>
            <h2 className="text-2xl font-display font-bold mb-2">Welcome to Badminton Club Manager</h2>
            <p className="text-muted-foreground mb-6">
              Let's get started by creating your club. Set up your courts, add members, and start managing matches.
            </p>
            <Link to="/dashboard/club">
              <Button size="lg" className="px-8">
                Create Your Club
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!clubId) {
    return <PageSpinner label="Select a club to continue." />;
  }

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in">
      {/* Header */}
      {isHeaderLoading ? (
        <PageHeaderSkeleton />
      ) : (
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-foreground break-words">
            Welcome back{welcomeName ? `, ${welcomeName}` : ''}!
          </h1>
          <p className="text-muted-foreground mt-1">
            {club?.slogan || 'Here\'s what\'s happening at your club.'}
          </p>
        </div>
      )}

      {clubId && user ? (
        <PlayerInsightsPanel
          clubId={clubId}
          packageCode={resolvedPackageCode}
          playerName={welcomeName || undefined}
        />
      ) : null}

      {/* Stats Grid */}
      {isStatsLoading ? (
        <StatGridSkeleton count={role === 'coach' ? 1 : 2} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <Card key={stat.label} className="stat-card">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                    <p className="mt-1 text-2xl sm:text-3xl font-display font-bold break-words">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                    <stat.icon className={stat.color} size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Active Matches */}
      <div>
        {isActiveMatchesLoading ? (
          <>
            <div className="mb-4">
              <Skeleton className="h-6 w-48" />
            </div>
            <CardGridSkeleton count={3} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" />
          </>
        ) : (
          <>
            <h2 className="text-lg sm:text-xl font-semibold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 bg-success rounded-full animate-pulse" />
              {role === 'admin' || role === 'coach' ? 'Active Matches' : 'Your Active Matches'} (
              {activeMatchesForUser.length})
            </h2>

            {activeMatchesForUser.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeMatchesForUser.map((match: any) => (
                  <Card key={match.id} className="border-success/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="relative flex items-center justify-between gap-3">
                        <span className="absolute left-1/2 -translate-x-1/2 text-center pointer-events-none">
                          Court {match.courtNumber}
                        </span>
                        <span className="text-xs text-success font-medium bg-success/10 px-2 py-1 rounded-full">
                          In Progress
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="text-sm text-muted-foreground space-y-3 text-center">
                        <div className="text-base font-semibold text-foreground">
                          {(match.team1 || []).map((id: string) => formatPlayerInline(match, id)).join(' & ') || 'Team 1'}
                        </div>
                        <div className="text-xl font-display font-bold text-foreground">VS</div>
                        <div className="text-base font-semibold text-foreground">
                          {(match.team2 || []).map((id: string) => formatPlayerInline(match, id)).join(' & ') || 'Team 2'}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-muted-foreground">{t('dashboard_no_active_matches_now', 'No active matches right now.')}</p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Club Statistics */}
      {isClubStatsLoading ? (
        <StatsSectionSkeleton cards={9} gridClassName="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center" />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 size={18} style={{ color: 'hsl(var(--sidebar-primary))' }} />
              Club Statistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-center">

              <div className="rounded-lg bg-muted/40 p-3 sm:p-4">
                <div className="flex justify-center mb-2">
                  <Calendar style={{ color: 'hsl(var(--sidebar-primary))' }} size={20} />
                </div>
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: 'hsl(var(--sidebar-primary))' }}>
                  {todayMatches.length}
                </p>
                <p className="text-sm text-muted-foreground">{t('dashboard_matches_played_today', 'Matches Played Today')}</p>
              </div>
              <div className="rounded-lg bg-muted/40 p-3 sm:p-4">
                <div className="flex justify-center mb-2">
                  <Activity style={{ color: 'hsl(var(--sidebar-primary))' }} size={20} />
                </div>
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: 'hsl(var(--sidebar-primary))' }}>
                  {ongoingMatches.length}
                </p>
                <p className="text-sm text-muted-foreground">Matches In Progress</p>
              </div>
              <div className="rounded-lg bg-muted/40 p-3 sm:p-4">
                <div className="flex justify-center mb-2">
                  <BarChart3 style={{ color: 'hsl(var(--sidebar-primary))' }} size={20} />
                </div>
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: 'hsl(var(--sidebar-primary))' }}>
                  {courtStats.avgActiveCourtsPerDay.toFixed(1)}
                </p>
                <p className="text-sm text-muted-foreground" data-no-runtime-localize="true">
                  {t('dashboard_avg_active_courts_day', 'Avg Active Courts / Day')}
                </p>
              </div>
            </div>
            <div className="mt-5">

              <div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {kpiSparklineCards.map((card) => (
                    <div key={card.key} className="rounded-lg border bg-muted/20 p-3">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <p className="text-xs text-muted-foreground">{card.label}</p>
                          <p className="text-2xl font-bold text-foreground">{card.value}</p>
                        </div>
                      </div>
                      <ChartContainer
                        config={{ value: { label: card.label, color: 'hsl(var(--sidebar-primary))' } }}
                        className="h-24 w-full"
                      >
                        <LineChart data={card.trend} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                          <YAxis hide padding={{ top: 16, bottom: 10 }} />
                          <Line
                            type="natural"
                            dataKey="value"
                            stroke="var(--color-value)"
                            strokeWidth={2}
                            dot={false}
                            activeDot={false}
                            connectNulls
                          />
                        </LineChart>
                      </ChartContainer>
                      <p className="text-xs text-muted-foreground mt-1">{card.helper}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {isPersonalizedStatsLoading ? (
        <StatsSectionSkeleton
          cards={7}
          gridClassName="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-center"
          withChart
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LineChartIcon size={18} className="text-primary" />
              Personalized Statistics for {welcomeName || 'You'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-center">
              <div className="rounded-lg bg-muted/40 p-3 sm:p-4">
                <div className="flex justify-center mb-2">
                  <Trophy style={{ color: 'hsl(var(--sidebar-primary))' }} size={20} />
                </div>
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: 'hsl(var(--sidebar-primary))' }}>
                  {userCompletedMatches.length}
                </p>
                <p className="text-sm text-muted-foreground">Total Matches Played Till-Date</p>
              </div>
              <div className="rounded-lg bg-muted/40 p-3 sm:p-4">
                <div className="flex justify-center mb-2">
                  <Calendar style={{ color: 'hsl(var(--sidebar-primary))' }} size={20} />
                </div>
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: 'hsl(var(--sidebar-primary))' }}>
                  {userMatchesToday.length}
                </p>
                <p className="text-sm text-muted-foreground">Matches Today</p>
              </div>
              <div className="rounded-lg bg-muted/40 p-3 sm:p-4">
                <div className="flex justify-center mb-2">
                  <CheckCircle2 style={{ color: 'hsl(var(--sidebar-primary))' }} size={20} />
                </div>
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: 'hsl(var(--sidebar-primary))' }}>
                  {userWinsToday}
                </p>
                <p className="text-sm text-muted-foreground">Matches Won Today</p>
              </div>
              <div className="rounded-lg bg-muted/40 p-3 sm:p-4">
                <div className="flex justify-center mb-2">
                  <TrendingUp style={{ color: 'hsl(var(--sidebar-primary))' }} size={20} />
                </div>
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: 'hsl(var(--sidebar-primary))' }}>
                  {maxWinsInADay}
                </p>
                <p className="text-sm text-muted-foreground">Max Matches Won on a Day</p>
              </div>
              <div className="rounded-lg bg-muted/40 p-3 sm:p-4">
                <div className="flex justify-center mb-2">
                  <TrendingDown style={{ color: 'hsl(var(--sidebar-primary))' }} size={20} />
                </div>
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: 'hsl(var(--sidebar-primary))' }}>
                  {maxLossesInADay}
                </p>
                <p className="text-sm text-muted-foreground">Max Matches Lost on a Day</p>
              </div>
              <div className="rounded-lg bg-muted/40 p-3 sm:p-4">
                <div className="flex justify-center mb-2">
                  <BarChart3 style={{ color: 'hsl(var(--sidebar-primary))' }} size={20} />
                </div>
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: 'hsl(var(--sidebar-primary))' }}>
                  {pointsPerMatch.scored}
                </p>
                <p className="text-sm text-muted-foreground">Points Scored per Match</p>
              </div>
              <div className="rounded-lg bg-muted/40 p-3 sm:p-4">
                <div className="flex justify-center mb-2">
                  <Activity style={{ color: 'hsl(var(--sidebar-primary))' }} size={20} />
                </div>
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: 'hsl(var(--sidebar-primary))' }}>
                  {pointsPerMatch.net}
                </p>
                <p className="text-sm text-muted-foreground">Net Points per Match</p>
              </div>

            </div>

            <div className="mt-6">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-2">
                  <p className="text-sm font-medium text-foreground">{t('dashboard_wins_losses_by_day', 'Wins & Losses by Day')}</p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground">
                        <HelpCircle size={14} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t('dashboard_wins_losses_tooltip', 'Shows your daily match results for the selected range.')}
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs sm:flex sm:items-center">
                  {DAY_RANGE_OPTIONS.map((days) => (
                    <Button
                      key={days}
                      variant={winsRangeDays === days ? 'default' : 'outline'}
                      size="sm"
                      className="w-full sm:w-auto"
                      onClick={() => setWinsRangeDays(days)}
                    >
                      {days}d
                    </Button>
                  ))}
                </div>
              </div>
              {lastResults.length > 0 ? (
                <div className="relative">
                  {winsCanPage && (
                    <>
                      <Button
                        variant="outline"
                        size="icon"
                        className="absolute left-1 top-1/2 z-10 h-8 w-8 -translate-y-1/2 rounded-full bg-background/90 shadow-sm sm:left-2"
                        onClick={() => setWinsChartPage((current) => Math.min(winsMaxPage, current + 1))}
                        disabled={winsChartPage >= winsMaxPage}
                        aria-label="Show older win-loss dates"
                      >
                        <ChevronLeft size={16} />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="absolute right-1 top-1/2 z-10 h-8 w-8 -translate-y-1/2 rounded-full bg-background/90 shadow-sm sm:right-2"
                        onClick={() => setWinsChartPage((current) => Math.max(0, current - 1))}
                        disabled={winsChartPage === 0}
                        aria-label="Show newer win-loss dates"
                      >
                        <ChevronRight size={16} />
                      </Button>
                    </>
                  )}
                  <ChartContainer
                    config={{
                      won: { label: t('leaderboard_won', 'Won'), color: 'hsl(var(--sidebar-primary))' },
                      lost: { label: t('leaderboard_lost', 'Lost'), color: '#fd5652' },
                    }}
                    className="h-56 w-full sm:h-48"
                  >
                    <BarChart data={visibleWinsResults} margin={{ top: 8, right: 24, left: 28, bottom: 0 }}>
                      <CartesianGrid vertical={false} />
                      <XAxis
                        dataKey="label"
                        tickLine={false}
                        axisLine={false}
                        interval={0}
                        angle={-35}
                        textAnchor="end"
                        height={56}
                        tickMargin={8}
                        padding={{ left: 24, right: 12 }}
                      />
                      <YAxis allowDecimals={false} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <ChartLegend content={<ChartLegendContent />} />
                      <Bar dataKey="won" fill="var(--color-won)" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="lost" fill="#fd5652" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ChartContainer>
                </div>
              ) : (
                <div className="rounded-lg border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
                  {t('dashboard_no_completed_matches_chart', 'No completed matches to chart yet.')}
                </div>
              )}
            </div>

            <div className="mt-6">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-2">
                  <p className="text-sm font-medium text-foreground">{t('dashboard_points_scored_vs_lost_day', 'Points Scored vs Points Lost by Day')}</p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground">
                        <HelpCircle size={14} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t('dashboard_points_chart_tooltip', 'Daily points scored and conceded for the selected range.')}
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs sm:flex sm:items-center">
                  {DAY_RANGE_OPTIONS.map((days) => (
                    <Button
                      key={days}
                      variant={pointsRangeDays === days ? 'default' : 'outline'}
                      size="sm"
                      className="w-full sm:w-auto"
                      onClick={() => setPointsRangeDays(days)}
                    >
                      {days}d
                    </Button>
                  ))}
                </div>
              </div>
              {lastPoints.length > 0 ? (
                <div className="relative">
                  {pointsCanPage && (
                    <>
                      <Button
                        variant="outline"
                        size="icon"
                        className="absolute left-1 top-1/2 z-10 h-8 w-8 -translate-y-1/2 rounded-full bg-background/90 shadow-sm sm:left-2"
                        onClick={() => setPointsChartPage((current) => Math.min(pointsMaxPage, current + 1))}
                        disabled={pointsChartPage >= pointsMaxPage}
                        aria-label="Show older point dates"
                      >
                        <ChevronLeft size={16} />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="absolute right-1 top-1/2 z-10 h-8 w-8 -translate-y-1/2 rounded-full bg-background/90 shadow-sm sm:right-2"
                        onClick={() => setPointsChartPage((current) => Math.max(0, current - 1))}
                        disabled={pointsChartPage === 0}
                        aria-label="Show newer point dates"
                      >
                        <ChevronRight size={16} />
                      </Button>
                    </>
                  )}
                  <ChartContainer
                    config={{
                      scored: { label: t('leaderboard_scored', 'Scored'), color: 'hsl(var(--sidebar-primary))' },
                      conceded: { label: t('leaderboard_lost', 'Lost'), color: '#fd5652' },
                    }}
                    className="h-56 w-full sm:h-48"
                  >
                    <BarChart data={visiblePointsResults} margin={{ top: 8, right: 24, left: 28, bottom: 0 }}>
                      <CartesianGrid vertical={false} />
                      <XAxis
                        dataKey="label"
                        tickLine={false}
                        axisLine={false}
                        interval={0}
                        angle={-35}
                        textAnchor="end"
                        height={56}
                        tickMargin={8}
                        padding={{ left: 24, right: 12 }}
                      />
                      <YAxis allowDecimals={false} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <ChartLegend content={<ChartLegendContent />} />
                      <Bar dataKey="scored" fill="var(--color-scored)" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="conceded" fill="#fd5652" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ChartContainer>
                </div>
              ) : (
                <div className="rounded-lg border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
                  {t('dashboard_no_completed_matches_chart', 'No completed matches to chart yet.')}
                </div>
              )}
            </div>

            <div className="mt-6">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-2">
                  <p className="text-sm font-medium text-foreground">Evaluation Progress Over Time</p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground">
                        <HelpCircle size={14} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t('dashboard_eval_progress_tooltip', 'Average line shows overall evaluation rating trend. Toggle categories to focus on strengths or gaps.')}
                    </TooltipContent>
                  </Tooltip>
                </div>
                {isInsightGraphsAvailable ? (
                  <div className="grid grid-cols-3 gap-2 text-xs sm:flex sm:items-center">
                    {[30, 90, 365].map((days) => (
                      <Button
                        key={days}
                        variant={evaluationRangeDays === days ? 'default' : 'outline'}
                        size="sm"
                        className="w-full sm:w-auto"
                        onClick={() => setEvaluationRangeDays(days)}
                      >
                        {days}d
                      </Button>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {t('dashboard_available_in_pro_elite', 'Available in Pro and Elite')}
                  </span>
                )}
              </div>
              {!isInsightGraphsAvailable ? (
                renderLockedInsightPlaceholder(
                  t('dashboard_eval_progress', 'Evaluation Progress Over Time'),
                  t('dashboard_eval_hint', 'Track average and category-wise evaluation changes across time ranges.'),
                  'evaluation',
                )
              ) : evaluationChartData.length > 0 ? (
                <>
                  <div className="mb-3 grid grid-cols-1 gap-2 text-xs sm:flex sm:flex-wrap sm:gap-3">
                    {[
                      { key: 'average', label: t('dashboard_average', 'Average'), disabled: true },
                      { key: 'technical', label: t('dashboard_technical', 'Technical') },
                      { key: 'movement', label: t('dashboard_movement', 'Movement') },
                      { key: 'tactical', label: t('dashboard_tactical', 'Tactical') },
                      { key: 'physical', label: t('dashboard_physical', 'Physical') },
                      { key: 'psychological', label: t('dashboard_psychological', 'Psychological') },
                    ].map((item) => (
                      <label key={item.key} className="flex items-center gap-2 rounded-md border border-border/60 px-2 py-1 sm:border-0 sm:px-0 sm:py-0">
                        <Checkbox
                          checked={visibleEvalKeys.has(item.key)}
                          disabled={item.disabled}
                          onCheckedChange={(checked) => {
                            setVisibleEvalKeys((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(item.key);
                              else next.delete(item.key);
                              return next;
                            });
                          }}
                        />
                        <span>{item.label}</span>
                      </label>
                    ))}
                  </div>
                    <ChartContainer
                      config={{
                        technical: { label: t('dashboard_technical', 'Technical'), color: 'hsl(var(--sidebar-primary))' },
                        movement: { label: t('dashboard_movement', 'Movement'), color: 'hsl(var(--success))' },
                        tactical: { label: t('dashboard_tactical', 'Tactical'), color: 'hsl(var(--warning))' },
                        physical: { label: t('dashboard_physical', 'Physical'), color: 'hsl(var(--accent))' },
                        psychological: { label: t('dashboard_psychological', 'Psychological'), color: 'hsl(var(--destructive))' },
                        average: { label: t('dashboard_average', 'Average'), color: 'hsl(var(--foreground))' },
                      }}
                    className="h-64 w-full sm:h-60"
                  >
                    <LineChart data={evaluationChartData} margin={{ top: 8, right: 24, left: 28, bottom: 0 }}>
                      <CartesianGrid vertical={false} />
                      <XAxis
                        dataKey="label"
                        tickLine={false}
                        axisLine={false}
                        interval="preserveStartEnd"
                        padding={{ left: 24, right: 12 }}
                      />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <ChartLegend content={<ChartLegendContent />} />
                      {visibleEvalKeys.has('technical') && (
                        <Line type="monotone" dataKey="technical" stroke="var(--color-technical)" strokeWidth={2} dot={{ r: 2 }} />
                      )}
                      {visibleEvalKeys.has('movement') && (
                        <Line type="monotone" dataKey="movement" stroke="var(--color-movement)" strokeWidth={2} dot={{ r: 2 }} />
                      )}
                      {visibleEvalKeys.has('tactical') && (
                        <Line type="monotone" dataKey="tactical" stroke="var(--color-tactical)" strokeWidth={2} dot={{ r: 2 }} />
                      )}
                      {visibleEvalKeys.has('physical') && (
                        <Line type="monotone" dataKey="physical" stroke="var(--color-physical)" strokeWidth={2} dot={{ r: 2 }} />
                      )}
                      {visibleEvalKeys.has('psychological') && (
                        <Line type="monotone" dataKey="psychological" stroke="var(--color-psychological)" strokeWidth={2} dot={{ r: 2 }} />
                      )}
                      <Line type="monotone" dataKey="average" stroke="var(--color-average)" strokeWidth={3} dot={{ r: 2 }} />
                    </LineChart>
                  </ChartContainer>
                </>
              ) : (
                <div className="rounded-lg border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
                  {t('dashboard_waiting_for_evaluations', 'Waiting for evaluations.')}
                </div>
              )}
            </div>

            <div className="mt-6">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-2">
                  <p className="text-sm font-medium text-foreground">
                    {t('dashboard_rank_last_days', 'Leaderboard Rank (Last {{days}} Days)').replace('{{days}}', String(rankRangeDays))}
                  </p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground">
                        <HelpCircle size={14} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t('dashboard_rank_tooltip', 'Rank is computed from the rating formula based on match results and evaluations.')}
                    </TooltipContent>
                  </Tooltip>
                </div>
                {isInsightGraphsAvailable ? (
                  <div className="flex flex-col gap-2 text-xs sm:flex-row sm:flex-wrap sm:items-center">
                    <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center">
                      {[30, 90, 365].map((days) => (
                        <Button
                          key={days}
                          variant={rankRangeDays === days ? 'default' : 'outline'}
                          size="sm"
                          className="w-full sm:w-auto"
                          onClick={() => setRankRangeDays(days)}
                        >
                          {days}d
                        </Button>
                      ))}
                    </div>
                    <label className="flex items-center gap-2 text-xs">
                      <Checkbox
                        checked={showRankAverage}
                        onCheckedChange={(checked) => setShowRankAverage(Boolean(checked))}
                      />
                      {t('dashboard_7d_avg', '7d Avg')}
                    </label>
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {t('dashboard_available_in_pro_elite', 'Available in Pro and Elite')}
                  </span>
                )}
              </div>
              {!isInsightGraphsAvailable ? (
                renderLockedInsightPlaceholder(
                  t('dashboard_rank_30d', 'Leaderboard Rank (Last 30 Days)'),
                  t('dashboard_rank_hint', 'See ranking movement and consistency trends over selectable periods.'),
                  'rank',
                )
              ) : leaderboardHistoryData.length > 0 ? (
                <ChartContainer
                  config={{
                    rank: { label: t('leaderboard_rank', 'Rank'), color: 'hsl(var(--sidebar-primary))' },
                    rank_avg: { label: t('dashboard_7d_avg', '7d Avg'), color: 'hsl(var(--accent))' },
                  }}
                  className="h-48 w-full"
                >
                  <LineChart data={leaderboardHistoryData} margin={{ top: 8, right: 24, left: 28, bottom: 0 }}>
                    <CartesianGrid vertical={false} />
                    <YAxis reversed allowDecimals={false} />
                    <XAxis
                      dataKey="label"
                      tickLine={false}
                      axisLine={false}
                      interval="preserveStartEnd"
                      padding={{ left: 24, right: 12 }}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    <Line
                      type="monotone"
                      dataKey="rank"
                      stroke="var(--color-rank)"
                      strokeWidth={2}
                      dot={{ r: 2 }}
                      connectNulls
                    />
                    {showRankAverage && (
                      <Line
                        type="monotone"
                        dataKey="rank_avg"
                        stroke="var(--color-rank_avg)"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                    )}
                  </LineChart>
                </ChartContainer>
              ) : (
                <div className="rounded-lg border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
                  {t('dashboard_no_leaderboard_history', 'No leaderboard history yet.')}
                </div>
              )}
            </div>

            <div className="mt-8 space-y-6">


              <div className="grid grid-cols-1 gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-sm font-medium text-foreground">
                      {t('dashboard_club_matches_by_selection_mode', 'Club Matches by Selection Mode (Last {{days}} Days)').replace('{{days}}', String(selectionModeRangeDays))}
                    </p>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground">
                          <HelpCircle size={14} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t('dashboard_selection_mode_tooltip', 'Share of matches by selection mechanism for the same period as the stacked chart.')}
                      </TooltipContent>
                    </Tooltip>
                    <div className="ml-auto flex items-center gap-2 text-xs">
                      {[7, 30, 90, 365].map((days) => (
                        <Button
                          key={days}
                          variant={selectionModeRangeDays === days ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSelectionModeRangeDays(days)}
                        >
                          {days}d
                        </Button>
                      ))}
                    </div>
                  </div>
                  {selectionModeShareData.length > 0 ? (
                    <ChartContainer
                      config={{
                        round_robin: { label: modeLabel('ROUND_ROBIN'), color: 'hsl(var(--sidebar-primary))' },
                        skill_based: { label: modeLabel('SKILL_BASED'), color: 'hsl(var(--success))' },
                        balanced_team: { label: modeLabel('BALANCED_TEAM'), color: 'hsl(var(--warning))' },
                        players_pick: { label: modeLabel('PLAYERS_PICK'), color: 'hsl(var(--accent))' },
                        unknown: { label: modeLabel('UNKNOWN'), color: 'hsl(var(--muted-foreground))' },
                      }}
                      className="h-56 w-full"
                    >
                      <PieChart>
                        <Pie
                          data={selectionModeShareData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          innerRadius={36}
                          paddingAngle={2}
                        >
                          {selectionModeShareData.map((entry) => (
                            <Cell key={entry.key} fill={entry.fill} />
                          ))}
                        </Pie>
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <ChartLegend content={<ChartLegendContent />} />
                      </PieChart>
                    </ChartContainer>
                  ) : (
                    <div className="rounded-lg border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
                      {t('dashboard_not_enough_match_data_pie', 'Not enough match data for pie chart.')}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <p className="text-sm font-medium text-foreground">{t('dashboard_my_evaluation_profile', 'My Evaluation Profile')}</p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground">
                        <HelpCircle size={14} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t('dashboard_evaluation_profile_tooltip', 'Shows your average score by evaluation category.')}
                    </TooltipContent>
                  </Tooltip>
                </div>
                {!isInsightGraphsAvailable ? (
                  renderLockedInsightPlaceholder(
                    t('dashboard_my_evaluation_profile', 'My Evaluation Profile'),
                    t('dashboard_profile_hint', 'View your category-wise skill profile and identify strengths and improvement areas.'),
                    'profile',
                  )
                ) : radarData.length > 0 ? (
                  <ChartContainer
                    config={{
                      value: { label: t('leaderboard_rating', 'Rating'), color: 'hsl(var(--sidebar-primary))' },
                    }}
                    className="h-72 w-full"
                  >
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="category" />
                      <PolarRadiusAxis angle={30} domain={[0, 5]} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Radar dataKey="value" stroke="var(--color-value)" fill="var(--color-value)" fillOpacity={0.3} />
                    </RadarChart>
                  </ChartContainer>
                ) : (
                  <div className="rounded-lg border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
                    {t('dashboard_evaluation_data_unavailable', 'Evaluation data is not available yet.')}
                  </div>
                )}
              </div>

              {isAdmin && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-sm font-medium text-foreground">{t('dashboard_queue_wait_distribution', 'Queue Wait Time Distribution')}</p>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground">
                          <HelpCircle size={14} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t('dashboard_queue_distribution_tooltip', 'Distribution of queue session durations grouped into wait-time buckets.')}
                      </TooltipContent>
                    </Tooltip>
                    <div className="ml-auto flex items-center gap-2 text-xs">
                      {[7, 30, 90, 365].map((days) => (
                        <Button
                          key={days}
                          variant={histogramRangeDays === days ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setHistogramRangeDays(days)}
                        >
                          {days}d
                        </Button>
                      ))}
                    </div>
                  </div>
                  {queueWaitHistogramData.length > 0 ? (
                    <ChartContainer
                      config={{
                        sessions: { label: t('dashboard_queue_sessions', 'Queue Sessions'), color: 'hsl(var(--sidebar-primary))' },
                      }}
                      className="h-52 w-full"
                    >
                      <BarChart data={queueWaitHistogramData} margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
                        <CartesianGrid vertical={false} />
                        <XAxis dataKey="label" tickLine={false} axisLine={false} interval={0} />
                        <YAxis allowDecimals={false} />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Bar dataKey="sessions" fill="var(--color-sessions)">
                          {queueWaitHistogramData.map((entry, index) => (
                            <Cell key={`${entry.label}-${index}`} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ChartContainer>
                  ) : (
                    <div className="rounded-lg border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
                      {t('dashboard_queue_history_unavailable', 'Queue history is not available yet.')}
                    </div>
                  )}
                </div>
              )}

              {isAdmin && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-sm font-medium text-foreground">{t('dashboard_court_usage_day_of_week', 'Court Usage by Day of Week')}</p>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground">
                          <HelpCircle size={14} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t('dashboard_court_usage_tooltip', 'Each cell shows how many completed matches were played on a court for a day of week.')}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  {courtHeatmap.rows.length > 0 ? (
                    <div className="overflow-x-auto">
                      <div className="min-w-[640px] border rounded-lg p-3 bg-muted/20">
                        <div
                          className="grid gap-2 text-xs"
                          style={{ gridTemplateColumns: `80px repeat(${courtHeatmap.days.length}, minmax(0, 1fr))` }}
                        >
                          <div className="font-medium text-muted-foreground">{t('dashboard_court', 'Court')}</div>
                          {courtHeatmap.days.map((day) => (
                            <div key={`day-${day}`} className="text-center font-medium text-muted-foreground">
                              {day}
                            </div>
                          ))}
                          {courtHeatmap.rows.map((row) => (
                            <React.Fragment key={`court-row-${row.court}`}>
                              <div className="font-medium text-muted-foreground self-center">
                                {t('dashboard_court_number', 'Court {{court}}').replace('{{court}}', String(row.court))}
                              </div>
                              {row.values.map((value, dayIdx) => {
                                const intensity = courtHeatmap.max > 0 ? value / courtHeatmap.max : 0;
                                const alpha = value > 0 ? 0.15 + intensity * 0.75 : 0.06;
                                return (
                                  <div
                                    key={`court-${row.court}-day-${dayIdx}`}
                                    className="h-9 rounded border flex items-center justify-center font-semibold"
                                    style={{ backgroundColor: `hsl(var(--sidebar-primary) / ${alpha})` }}
                                    title={t('dashboard_court_match_count_title', 'Court {{court}} {{day}}: {{count}} {{matchWord}}')
                                      .replace('{{court}}', String(row.court))
                                      .replace('{{day}}', String(courtHeatmap.days[dayIdx]))
                                      .replace('{{count}}', String(value))
                                      .replace('{{matchWord}}', value === 1 ? t('dashboard_match_singular', 'match') : t('dashboard_match_plural', 'matches'))}
                                  >
                                    {value || ''}
                                  </div>
                                );
                              })}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
                      {t('dashboard_no_completed_matches_heatmap', 'No completed matches to build a heatmap.')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Role-specific sections */}
      {role === 'coach' && null}

    </div>
  );
}
